/**
 * QAI Settings API Service
 *
 * API integration layer for Q.AI Admin Settings Management
 * Endpoints: /api/admin/qai/settings/* (FIX Engine proxies to DataFeed)
 *
 * Task: T.6 - QAI Settings Admin Page
 */

import axios from 'axios';
import { getQAIAdminConfig } from '../config';

// Get routing configuration (DataFeed direct or FIX Engine proxy)
const { baseUrl, pathPrefix } = getQAIAdminConfig();

/**
 * Get auth token from localStorage
 */
const getAuthToken = () => {
  const token = localStorage.getItem('token');
  if (!token) {
    throw new Error('No authentication token found. Please log in.');
  }
  return token;
};

/**
 * Standard headers for API requests
 */
const getHeaders = (token) => ({
  'Authorization': `Bearer ${token}`,
  'Content-Type': 'application/json',
  'Accept': 'application/json',
});

// ============================================================================
// SETTINGS API
// ============================================================================

/**
 * Get all settings grouped by category
 *
 * DataFeed returns flat array: { data: [ {settingKey, category, ...}, ... ] }
 * Frontend expects grouped: { categories: { claude: [...], tokens: [...], ... } }
 *
 * @returns {Promise<Object>} Settings grouped by category
 */
export const getAllSettings = async () => {
  try {
    const token = getAuthToken();
    const url = `${baseUrl}${pathPrefix}/settings`;
    console.log('[settingsApi] getAllSettings:', url);

    const response = await axios.get(url, {
      headers: getHeaders(token)
    });

    console.log('[settingsApi] getAllSettings response:', response.data);

    const responseData = response.data?.data || response.data;

    // If DataFeed already returns categories object, use it directly
    if (responseData.categories && typeof responseData.categories === 'object') {
      return {
        success: true,
        categories: responseData.categories,
        totalCount: responseData.totalCount || 0
      };
    }

    // Otherwise, DataFeed returns flat array - transform to category-grouped structure
    // Handle both array directly and { settings: [...] } wrapper
    const settingsArray = Array.isArray(responseData)
      ? responseData
      : (responseData.settings || []);

    // Group settings by category
    const categories = {};
    settingsArray.forEach(setting => {
      const category = setting.category || 'general';
      if (!categories[category]) {
        categories[category] = [];
      }
      categories[category].push(setting);
    });

    console.log('[settingsApi] Transformed categories:', Object.keys(categories));

    return {
      success: true,
      categories,
      totalCount: settingsArray.length
    };

  } catch (error) {
    console.error('[settingsApi] getAllSettings error:', error);
    return {
      success: false,
      error: error.response?.data?.message || error.message || 'Failed to fetch settings'
    };
  }
};

/**
 * Get single setting by key
 *
 * @param {string} key - Setting key (e.g., 'claude_max_tokens')
 * @returns {Promise<Object>} Single setting object
 */
export const getSettingByKey = async (key) => {
  try {
    const token = getAuthToken();
    const url = `${baseUrl}${pathPrefix}/settings/${key}`;
    console.log('[settingsApi] getSettingByKey:', url);

    const response = await axios.get(url, {
      headers: getHeaders(token)
    });

    console.log('[settingsApi] getSettingByKey response:', response.data);

    const responseData = response.data?.data || response.data;
    return {
      success: true,
      setting: responseData
    };

  } catch (error) {
    console.error('[settingsApi] getSettingByKey error:', error);
    return {
      success: false,
      error: error.response?.data?.message || error.message || 'Failed to fetch setting'
    };
  }
};

/**
 * Update setting value
 *
 * @param {string} key - Setting key
 * @param {string} value - New value (always sent as string)
 * @returns {Promise<Object>} Updated setting
 */
export const updateSetting = async (key, value) => {
  try {
    const token = getAuthToken();
    const url = `${baseUrl}${pathPrefix}/settings/${key}`;
    console.log('[settingsApi] updateSetting:', url, { value });

    const response = await axios.put(url, { value: String(value) }, {
      headers: getHeaders(token)
    });

    console.log('[settingsApi] updateSetting response:', response.data);

    return {
      success: true,
      message: response.data?.message || `Setting '${key}' updated successfully`,
      setting: response.data?.setting || response.data?.data
    };

  } catch (error) {
    console.error('[settingsApi] updateSetting error:', error);
    return {
      success: false,
      error: error.response?.data?.message || error.message || 'Failed to update setting'
    };
  }
};

/**
 * Reset setting to default value
 *
 * @param {string} key - Setting key
 * @returns {Promise<Object>} Reset result
 */
export const resetSetting = async (key) => {
  try {
    const token = getAuthToken();
    const url = `${baseUrl}${pathPrefix}/settings/${key}/reset`;
    console.log('[settingsApi] resetSetting:', url);

    const response = await axios.post(url, {}, {
      headers: getHeaders(token)
    });

    console.log('[settingsApi] resetSetting response:', response.data);

    return {
      success: true,
      message: response.data?.message || `Setting '${key}' reset to default`,
      setting: response.data?.setting || response.data?.data
    };

  } catch (error) {
    console.error('[settingsApi] resetSetting error:', error);
    return {
      success: false,
      error: error.response?.data?.message || error.message || 'Failed to reset setting'
    };
  }
};

/**
 * Get settings by category
 *
 * @param {string} category - Category name: 'claude', 'tokens', 'prompts', 'context'
 * @returns {Promise<Object>} Settings in category
 */
export const getSettingsByCategory = async (category) => {
  try {
    const token = getAuthToken();
    const url = `${baseUrl}${pathPrefix}/settings/category/${category}`;
    console.log('[settingsApi] getSettingsByCategory:', url);

    const response = await axios.get(url, {
      headers: getHeaders(token)
    });

    console.log('[settingsApi] getSettingsByCategory response:', response.data);

    const responseData = response.data?.data || response.data;
    return {
      success: true,
      settings: Array.isArray(responseData) ? responseData : responseData.settings || []
    };

  } catch (error) {
    console.error('[settingsApi] getSettingsByCategory error:', error);
    return {
      success: false,
      error: error.response?.data?.message || error.message || 'Failed to fetch category settings'
    };
  }
};

// ============================================================================
// SETTINGS DEFINITIONS (Reference)
// ============================================================================

export const SETTINGS_CATEGORIES = {
  claude: {
    label: 'Claude Configuration',
    description: 'AI model settings and response parameters',
    icon: 'SmartToy'
  },
  tokens: {
    label: 'Token Costs',
    description: 'Token consumption rates for different operations',
    icon: 'Token'
  },
  context: {
    label: 'Context Settings',
    description: 'Data limits for AI context window',
    icon: 'DataObject'
  },
  prompts: {
    label: 'Prompt Settings',
    description: 'Prompt caching and optimization',
    icon: 'Description'
  }
};

export const SETTING_TYPES = {
  int: { label: 'Integer', inputType: 'number' },
  string: { label: 'Text', inputType: 'text' },
  bool: { label: 'Boolean', inputType: 'switch' },
  json: { label: 'JSON', inputType: 'textarea' }
};

export default {
  getAllSettings,
  getSettingByKey,
  updateSetting,
  resetSetting,
  getSettingsByCategory,
  SETTINGS_CATEGORIES,
  SETTING_TYPES
};
